// Storage keys matching Flutter SecureStorageKey
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_UID: 'userUId',
  ORGANIZATION_UID: 'organizationUId',
  USER_ROLE: 'userRole',
  USER_NAME: 'userName',
  USER_EMAIL: 'userEmail',
  ORGANIZATION_NAME: 'organizationName',
  STATUS: 'status',
  NOTIFICATION_COUNT: 'notificationCount',
  DEVICE_TOKEN: 'deviceToken',
  FEATURE_FLAGS: 'featureFlags',
  USER_PERMISSIONS: 'userPermissions',
} as const;

type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

class StorageService {
  private storage: Storage;

  constructor() {
    // Use localStorage for persistence
    // In production, consider using secure-ls or similar for encryption
    this.storage = window.localStorage;
  }

  // Get string value
  getString(key: StorageKey): string | null {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      console.error(`Error getting ${key} from storage:`, error);
      return null;
    }
  }

  // Set string value
  setString(key: StorageKey, value: string): void {
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
    }
  }

  // Get JSON value
  getJSON<T>(key: StorageKey): T | null {
    try {
      const value = this.storage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error(`Error parsing ${key} from storage:`, error);
      return null;
    }
  }

  // Set JSON value
  setJSON<T>(key: StorageKey, value: T): void {
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error setting ${key} in storage:`, error);
    }
  }

  // Remove item
  remove(key: StorageKey): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from storage:`, error);
    }
  }

  // Clear all storage
  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  // Clear auth-related data
  clearAuthData(): void {
    this.remove(STORAGE_KEYS.ACCESS_TOKEN);
    this.remove(STORAGE_KEYS.REFRESH_TOKEN);
    this.remove(STORAGE_KEYS.USER_UID);
    this.remove(STORAGE_KEYS.ORGANIZATION_UID);
    this.remove(STORAGE_KEYS.USER_ROLE);
    this.remove(STORAGE_KEYS.USER_NAME);
    this.remove(STORAGE_KEYS.USER_EMAIL);
    this.remove(STORAGE_KEYS.ORGANIZATION_NAME);
    this.remove(STORAGE_KEYS.STATUS);
    this.remove(STORAGE_KEYS.USER_PERMISSIONS);
  }

  // Check if key exists
  has(key: StorageKey): boolean {
    return this.getString(key) !== null;
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Export for named imports
export { STORAGE_KEYS as StorageKeys };
